'use client';

import cx from 'classnames';
import { MapPin, Phone } from 'lucide-react';
import { testDriveCenterImages } from '~/constants/dealership/testDriveCenter/assets';
import {
  useDealershipAddress,
  useDealershipConfiguration,
  useDealershipDepartmentHours,
  useDealershipPhones
} from '~/context/dealership/StaticContext';
import optimizeImage from '~/utilities/optimizeImage';
import styles from '../TestDriveCenter.module.css';

const section = 'Test Drive Center - Visit';

// Full-resolution source (2400px) served as optimized webp. The card goes
// full-width on mobile retina screens, so no width cap — capping at ~1000px
// upscaled and looked pixelated there.
const storefrontImage = optimizeImage(testDriveCenterImages.storefront, {
  format: 'auto',
  optimize: 'medium'
});

export default function TestDriveCenterVisit() {
  const configuration = useDealershipConfiguration();
  const dealershipAddress = useDealershipAddress();
  const phones = useDealershipPhones();
  const hours = useDealershipDepartmentHours();

  const dealershipName = configuration.fullName ?? configuration.name ?? '';
  const phone = phones[0]?.number ?? '';
  const street = [dealershipAddress.street1, dealershipAddress.street2]
    .filter(Boolean)
    .join(', ');
  const cityStateZip = [
    [dealershipAddress.city, dealershipAddress.state]
      .filter(Boolean)
      .join(', '),
    dealershipAddress.zip
  ]
    .filter(Boolean)
    .join(' ');
  const addressLine = [street, cityStateZip].filter(Boolean).join(' · ');

  return (
    <section
      className="relative overflow-hidden bg-[var(--cvna-navy)] scroll-mt-64"
      id="visit"
    >
      <div className="mx-auto grid max-w-[1320px] grid-cols-[1.1fr_1fr] items-center gap-[clamp(40px,5vw,80px)] px-[clamp(20px,4vw,56px)] py-[clamp(72px,10vh,128px)] max-[920px]:grid-cols-1">
        <div
          className={cx(
            styles.visitImageCard,
            styles.animIn,
            'aspect-[4/3] overflow-hidden rounded-[20px]'
          )}
        >
          <img
            alt={`The ${dealershipName} Test Drive Center`}
            className="block h-full w-full object-cover"
            src={storefrontImage}
          />
        </div>
        <div className={styles.animIn}>
          <div className={styles.eyebrow}>{'Visit · Park Cities'}</div>
          <h2 className="mb-28 font-[family-name:var(--font-display)] text-[clamp(34px,4.4vw,62px)] font-black uppercase leading-[0.92] tracking-[-0.02em] text-white">
            Come see it in person
          </h2>

          <div className="mb-[22px] flex items-start gap-12">
            <MapPin
              className="mt-xs flex-none"
              color="var(--cvna-blue-bright)"
              size={22}
            />
            <div className="text-[17px] font-semibold leading-[1.5] text-white">
              {dealershipName}
              <br />
              <span className="font-normal text-[rgba(255,255,255,0.72)]">
                {addressLine}
              </span>
            </div>
          </div>

          {phone && (
            <div className="mb-[22px] flex items-start gap-12">
              <Phone
                className="mt-xs flex-none"
                color="var(--cvna-blue-bright)"
                size={22}
              />
              <div className="text-[17px] font-semibold leading-[1.5] text-white">
                <a
                  data-adobe-id="click-to-call"
                  data-analytics-id={`${section} - Phone`}
                  data-testid="test-drive-center-visit-phone"
                  href={`tel:${phone}`}
                >
                  {phone}
                </a>
                <br />
                <span className="font-normal text-[rgba(255,255,255,0.72)]">
                  Sales · Service · Parts
                </span>
              </div>
            </div>
          )}

          <div className="mb-28 grid grid-cols-2 gap-x-36 gap-y-0 border-y border-[rgba(255,255,255,0.18)] py-8 max-[920px]:grid-cols-1 max-[920px]:gap-0">
            {hours.map(table => (
              <div key={table.label}>
                <div className="pb-8 pt-12 text-[11.5px] font-bold uppercase leading-none tracking-[0.14em] text-[var(--cvna-blue-bright)]">
                  {table.label}
                </div>
                {table.daysHours.map(row => (
                  <div
                    key={row.days}
                    className={cx(
                      'flex justify-between gap-12 border-t border-[rgba(255,255,255,0.1)] py-8 text-[14.5px] font-medium leading-[1.4]',
                      /closed/i.test(row.hours)
                        ? 'text-[rgba(255,255,255,0.55)]'
                        : 'text-[rgba(255,255,255,0.9)]'
                    )}
                  >
                    <span>{row.days}</span>
                    <span className="font-semibold">{row.hours}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
